import {Link, useCart, useUrl} from '@shopify/hydrogen';
import {
  COUNTRIES_MANAGED_BY_DISTRIBUTOR,
  COUNTRIES_WE_SHIP_TO,
} from '../../constants';
import useGeo from '../../hooks/useGeo';
import CircleOutlineButton from '../elements/CircleOutlineButton';
import {useAtom} from 'jotai';
import {
  getShippingUpdatesFormDialogAtom,
  countryManagedByDistributorDialogAtom,
} from '../../jotai';
import clsx from 'clsx';

type Props = {
  handleClick?: () => void;
};

/**
 * A client component that defines the behavior when a user toggles a cart
 */
export default function CartToggle({handleClick}: Props) {
  const {href} = useUrl();
  const {totalQuantity} = useCart();
  const {isShippable, isManagedByDistributor} = useGeo();
  const [, setShowGetShippingUpdatesForm] = useAtom(
    getShippingUpdatesFormDialogAtom,
  );
  const [, setShowCountryManagedByDistributor] = useAtom(
    countryManagedByDistributorDialogAtom,
  );
  const isException = !isShippable || isManagedByDistributor;

  function handleException() {
    if (!isShippable && !isManagedByDistributor) {
      setShowGetShippingUpdatesForm(true);
    }
    if (isManagedByDistributor) {
      setShowCountryManagedByDistributor(true);
    }
  }

  return (
    <Link
      to={isException ? href : '/cart'}
      rel="nofollow"
      onClick={isException ? handleException : handleClick}
    >
      <CircleOutlineButton aria-controls="cart">
        <div className={clsx('relative', isException && 'opacity-60')}>
          <svg
            className="flip transition-standard"
            width="24"
            height="22"
            viewBox="0 0 24 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.4385 5.6435C21.9831 5.13213 21.3236 4.83791 20.6296 4.83659H5.05201L4.81265 3.4851C4.65407 2.57912 4.17192 1.75695 3.45176 1.16471C2.7316 0.57269 1.82009 0.2484 0.878445 0.250006H0.769205C0.556694 0.252247 0.354283 0.339193 0.209246 0.490897C0.0639747 0.642825 -0.0110673 0.845849 0.00132311 1.05313C0.0137159 1.27139 0.111709 1.47687 0.275111 1.62634C0.438506 1.77603 0.65446 1.85827 0.878475 1.85625C1.43201 1.85536 1.96811 2.04583 2.39153 2.39406C2.81494 2.74206 3.09835 3.22541 3.19176 3.75828L5.40065 16.2325C5.5411 16.9933 6.05883 17.6364 6.78245 17.9495C6.3517 18.338 6.09396 18.8761 6.06411 19.4479C6.03451 20.02 6.23532 20.5805 6.62361 21.0098C7.01192 21.439 7.55698 21.7025 8.14195 21.7442C8.72693 21.7859 9.3055 21.6023 9.75347 21.2328C10.2014 20.8631 10.4837 20.3369 10.5393 19.7668C10.595 19.1968 10.4197 18.628 10.0513 18.1823H16.6995H16.6993C16.3794 18.566 16.2036 19.0453 16.2013 19.5401C16.1997 20.1211 16.4351 20.6789 16.8553 21.0903C17.2756 21.5016 17.8461 21.7329 18.4411 21.7329C19.0362 21.7329 19.607 21.5017 20.0272 21.0903C20.4473 20.6788 20.6826 20.1211 20.681 19.5401C20.6787 19.0453 20.5029 18.566 20.183 18.1823H20.3221H20.3218C20.7762 18.1823 21.1443 17.8229 21.1443 17.3792C21.1443 16.9357 20.7762 16.5761 20.3218 16.5761H7.77338C7.40229 16.5767 7.08466 16.3161 7.02156 15.9589L6.83842 14.9246H18.5062C19.4763 14.9237 20.4133 14.5793 21.1431 13.9554C21.8731 13.3315 22.3465 12.4703 22.4758 11.5317L23.0146 7.49722H23.0148C23.106 6.82695 22.896 6.15135 22.4388 5.64361L22.4385 5.6435ZM18.4416 20.121C18.2011 20.1212 17.984 19.9798 17.892 19.7629C17.7997 19.5457 17.8505 19.2961 18.0203 19.1298C18.1903 18.9636 18.4462 18.9138 18.6686 19.0034C18.8907 19.0933 19.0358 19.3048 19.036 19.5397C19.0358 19.8604 18.77 20.1203 18.4416 20.121V20.121ZM8.30958 20.121C8.06907 20.1212 7.85197 19.9798 7.75995 19.7629C7.66769 19.5457 7.71841 19.2961 7.88823 19.1298C8.05829 18.9636 8.31417 18.9138 8.53654 19.0034C8.75869 19.0933 8.90373 19.3048 8.90396 19.5397C8.90373 19.8604 8.63798 20.1203 8.30958 20.121V20.121ZM20.8445 11.3233C20.7676 11.8757 20.4883 12.3823 20.0583 12.749C19.6282 13.1158 19.0765 13.3179 18.5055 13.3177H6.55337L5.33589 6.44238H20.6291C20.8476 6.4453 21.0546 6.53874 21.1985 6.69941C21.3424 6.86008 21.4096 7.07296 21.3835 7.28472L20.8445 11.3233Z"
              fill="white"
            />
          </svg>
          <div
            className="blockH10 absolute -right-2 -top-2 flex h-[20px] min-w-[20px] items-center justify-center rounded-full bg-orange px-1 font-bold text-white"
            data-testid="cart-quantity"
          >
            {totalQuantity}
          </div>
        </div>
      </CircleOutlineButton>
    </Link>
  );
}
